import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import {
  agencyHasFeature,
  isAgencyAdmin,
  isAgencyUser,
  token,
  user,
} from "../../../modules/auth";
import AgencyNewsFeed from "./AgencyNewsFeed";
import SiteVisitsDash from "./SiteVisitsDash";
import AgencySearchBanner from "./AgencySearchBanner";
import AgencyEventsCalendarDash from "./AgencyEventsCalendarDash";
import { listFormatter, nameFormatter } from "../../../utils";
import AgencySnapshotDashReport from "./AgencySnapshotDashReport";
import { agencyObject } from "../../../modules/permissions";
import { AGENCY_PERMISSIONS } from "../../../constants";
import { refreshUser } from "../../../modules/auth-thunks";
import AgencySnapshotDashChart from "./AgencySnapshotDashChart";
import AgencyDashUpcomingEvents from "./AgencyDashUpcomingEvents";
import AgencyDashboardNavigation from "./AgencyDashboardNavigation";
import AgencyInformation from "./AgencyInformation";
import { ButtonTertiary } from "../../Button";
import * as SITE_VISIT_ROUTES from "../../../Router/site_visit_routes";
import { getNewsletterSettings } from "../../../services/newsletterSettingsService";

const GridBlockComponent = ({
  title,
  children,
  id,
  fullWidth = false,
  links = null,
}) => {
  return (
    <div id={id} className={`w-full px-2 mb-4 ${fullWidth ? "" : "xl:w-1/2"}`}>
      <div className="p-4 bg-white rounded h-full">
        <div className="flex">
          {title && <h2 className="text-2xl font-bold">{title}</h2>}
          {links && <div className="ml-auto">{links}</div>}
        </div>
        {children}
      </div>
    </div>
  );
};

function AgencyDashboard({
  token,
  selectedAgencies,
  agency,
  user,
  isAgencyAdmin,
  isAgencyUser,
  canAccessAgencyCalendar,
  canAccessAgencyDashboardSnapshot,
}) {
  const location = useLocation();
  const [agencies, setAgencies] = useState([]);
  const [newsletterTitle, setNewsletterTitle] = useState();
  const [newsletterLink, setNewsletterLink] = useState();
  const [newsletterPanelTitle, setNewsletterPanelTitle] = useState();

  useEffect(() => {
    let isMounted = true;
    const fetchSettings = async () => {
      const { result } = await getNewsletterSettings(token);

      if (!isMounted || !result) return;
      const agencyNewsletterTitle = result.find(
        value => value.key === "agency_newsletter_title"
      );
      const agencyNewsletterLink = result.find(
        value => value.key === "agency_newsletter_link"
      );
      if (agencyNewsletterTitle) {
        setNewsletterTitle(agencyNewsletterTitle.value);
        setNewsletterPanelTitle(agencyNewsletterTitle.label);
      }
      if (agencyNewsletterLink) {
        setNewsletterLink(agencyNewsletterLink.value);
      }
    };

    void refreshUser(true);
    void fetchSettings();

    return () => {
      isMounted = false;
    };
  }, [token]);

  useEffect(() => {
    if (Array.isArray(selectedAgencies) && selectedAgencies.length > 0) {
      let otherAgencies = selectedAgencies.map(a => ({
        id: a.id,
        name: a.name,
      }));
      const agencyExists = otherAgencies.some(a => a.id === agency.id);
      if (agencyExists) {
        otherAgencies = otherAgencies.filter(a => a.id !== agency.id);
        setAgencies([agency, ...otherAgencies]);
      } else {
        setAgencies([...otherAgencies]);
      }
    } else {
      setAgencies([agency]);
    }
  }, [selectedAgencies]);

  const getWelcomeMessage = () => {
    if (
      agencies.length === 0 ||
      (agencies.length === 1 && agencies[0].id === agency.id)
    ) {
      return (
        <>
          Welcome <strong>{agency?.name || ""}</strong>
        </>
      );
    } else if (agencies.length === 1 && agencies[0].id !== agency.id) {
      return (
        <>
          Welcome <strong>{agencies[0].name}</strong> (managed by {agency?.name}
          )
        </>
      );
    }
  };

  return (
    <div
      key={location.key}
      className="m-0 xxl:p-16 p-4 md:p-8 agency-admin__dashboard__hero"
      style={{
        backgroundImage: "url(/img/corp-edge-reports-bg-blue-darker.jpg)",
        backgroundPosition: "0 -80px",
        width: "100vw",
        position: "relative",
        left: "50%",
        right: "50%",
        marginLeft: "-50vw",
        marginRight: "-50vw",
      }}
    >
      <div className="container">
        <p className={"mb-2 text-white"}>{getWelcomeMessage()}</p>

        {agencies && agencies?.length > 1 ? (
          <div className={"bg-blue-light p-2 my-4"}>
            You are viewing data for
            <> {listFormatter.format(agencies?.map(a => a.name))}</>
          </div>
        ) : null}
        <h1 className={"text-5xl text-white"}>
          My Dashboard
        </h1>

        <AgencyDashboardNavigation
          agencies={agencies}
          newsletterTitle={newsletterTitle}
          newsletterLink={newsletterLink}
          newsletterPanelTitle={newsletterPanelTitle}
        />

        {/*{isAgencyAdmin && (*/}
        {/*  <AgencyAdminQuickSearch*/}
        {/*    user={user}*/}
        {/*    showSearchForVenues={canAccessAgencyDashboardSnapshot}*/}
        {/*  />*/}
        {/*)}*/}

        <div className="flex flex-wrap flex-row -mx-2 mt-8">
          {isAgencyAdmin && canAccessAgencyDashboardSnapshot ? (
            <>
              <GridBlockComponent>
                <AgencySnapshotDashReport agencies={agencies} />
              </GridBlockComponent>
            </>
          ) : null}

          {isAgencyAdmin && canAccessAgencyDashboardSnapshot ? (
            <GridBlockComponent>
              <AgencySnapshotDashChart agencies={agencies} />
            </GridBlockComponent>
          ) : null}

          {isAgencyUser && isAgencyAdmin && canAccessAgencyCalendar ? (
            <>
              {/* Confirmed Events Calendar */}
              <GridBlockComponent title={"Confirmed Events"}>
                <AgencyEventsCalendarDash agencies={agencies} />
              </GridBlockComponent>
            </>
          ) : null}

          {isAgencyAdmin && (
            <GridBlockComponent title={"Agency News"}>
              <AgencyNewsFeed
                newsletterTitle={newsletterTitle}
                newsletterLink={newsletterLink}
                newsletterPanelTitle={newsletterPanelTitle}
              />
            </GridBlockComponent>
          )}

          <GridBlockComponent title={"Search for Venues"} id={"searchPanel"}>
            <AgencySearchBanner agencies={agencies} />
          </GridBlockComponent>

          {isAgencyUser && !isAgencyAdmin && (
            <GridBlockComponent title={"Agency News"}>
              <AgencyNewsFeed
                newsletterTitle={newsletterTitle}
                newsletterLink={newsletterLink}
                newsletterPanelTitle={newsletterPanelTitle}
              />
            </GridBlockComponent>
          )}

          {isAgencyUser && !isAgencyAdmin && canAccessAgencyCalendar ? (
            <>
              {/* Confirmed Events Calendar */}
              <GridBlockComponent title={"Confirmed Events"}>
                <AgencyEventsCalendarDash agencies={agencies} />
              </GridBlockComponent>
            </>
          ) : null}

          <GridBlockComponent>
            <AgencyDashUpcomingEvents agencies={agencies} />
          </GridBlockComponent>

          <GridBlockComponent title={"Agency Information"}>
            <AgencyInformation />
          </GridBlockComponent>

          <GridBlockComponent
            title={"Latest Site Visits"}
            links={
              <>
                <ButtonTertiary
                  style={{ width: "inherit" }}
                  classes="text-md font-bold mr-2"
                >
                  <Link
                    to={SITE_VISIT_ROUTES.FRONTEND.CREATE}
                    className={"text-white"}
                  >
                    Create a Report
                  </Link>
                </ButtonTertiary>
              </>
            }
          >
            <SiteVisitsDash />
          </GridBlockComponent>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = state => {
  return {
    ...state.selectedAgencies,
    user: user(state),
    agency: agencyObject(state.auth),
    isAgencyAdmin: isAgencyAdmin(state),
    isAgencyUser: isAgencyUser(state),
    canAccessAgencyCalendar: agencyHasFeature(
      state.auth,
      AGENCY_PERMISSIONS.CAN_VIEW_AGENTS_CALENDAR
    ),
    canAccessAgencyDashboardSnapshot: agencyHasFeature(
      state.auth,
      AGENCY_PERMISSIONS.CAN_VIEW_AGENCY_DASHBOARD_SNAPSHOT
    ),
    token: token(state),
  };
};

export default connect(mapStateToProps, null)(AgencyDashboard);
